import React from "react";
import { useTranslation } from "react-i18next";
import { HiBan, HiReply } from "react-icons/hi";
import ActionButton from "../../../atoms/ActionButton";
import CopyToClipboardButton from "../../../atoms/CopyToClipboardButton";
import {
  CancelSubscriptionInfo,
  InvoiceLinkVariant,
  PlanEntry,
  isRecurringPlan,
} from "./paymentHistoryUtils";

interface PaymentStepActionsProps {
  entry: PlanEntry;
  planDescription: string;
  planPaymentType: string;
  canCreatePaymentLink: boolean;
  invoiceLinks: Record<string, string | null>;
  onFetchInvoiceLink: (
    invoiceId: string,
    variant: InvoiceLinkVariant
  ) => Promise<string | null>;
  onOpenRefund: (
    paymentStepIdentification: string,
    amount: number,
    currency: string,
    description: string
  ) => void;
  onCancelSubscription?: (info: CancelSubscriptionInfo) => void;
  compact?: boolean;
}

const PaymentStepActions: React.FC<PaymentStepActionsProps> = ({
  entry,
  planDescription,
  planPaymentType,
  canCreatePaymentLink,
  invoiceLinks,
  onFetchInvoiceLink,
  onOpenRefund,
  onCancelSubscription,
  compact = false,
}) => {
  const { t } = useTranslation();
  const { payment, paymentData, refundedTotal } = entry;
  const isRecurring = isRecurringPlan(planPaymentType);

  const canCancel =
    !!onCancelSubscription &&
    isRecurring &&
    payment.paymentStatusEnum === "ACTIVE" &&
    !!payment.paymentRequestUniqueLink &&
    canCreatePaymentLink;

  const canRefund =
    (payment.paymentStatusEnum === "ACTIVE" ||
      payment.paymentStatusEnum === "SUCCEEDED") &&
    paymentData.amount > 0 &&
    refundedTotal < paymentData.amount &&
    canCreatePaymentLink;

  const invoiceIntent: InvoiceLinkVariant | null =
    !paymentData.invoiceIdentification
      ? null
      : ["SUCCEEDED", "ENDED", "ACTIVE", "REFUNDED"].includes(
          payment.paymentStatusEnum
        )
      ? "receipt"
      : ["UNPAID", "ERROR"].includes(payment.paymentStatusEnum)
      ? "payment"
      : null;
  const canCopyReceipt = invoiceIntent !== null;

  if (!canCancel && !canRefund && !canCopyReceipt) return null;

  return (
    <div
      className={
        compact
          ? "mt-2 flex flex-wrap items-center gap-2"
          : "mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center justify-end gap-2"
      }
    >
      {canCancel && (
        <ActionButton
          onClick={() => {
            if (!canCreatePaymentLink) return;
            onCancelSubscription!({
              uniqueLink: payment.paymentRequestUniqueLink!,
              description: planDescription,
              amount: paymentData.amount,
              currency: paymentData.currency,
              paymentType: planPaymentType,
            });
          }}
          disabled={!canCreatePaymentLink}
          colorScheme="orange"
          title={
            <span className="inline-flex items-center gap-1.5">
              <HiBan size={14} />
              <span className="hidden sm:inline">
                {t("paymentModule.userPaymentHistory.cancelSubscription")}
              </span>
            </span>
          }
          aria-label={
            t(
              "paymentModule.userPaymentHistory.cancelSubscriptionAria"
            ) as string
          }
        />
      )}
      {canRefund && (
        <ActionButton
          onClick={() =>
            onOpenRefund(
              payment.paymentStepIdentification,
              paymentData.amount - refundedTotal,
              paymentData.currency,
              planDescription
            )
          }
          disabled={!canCreatePaymentLink}
          colorScheme="red"
          title={
            <span className="inline-flex items-center gap-1.5">
              <HiReply size={14} />
              <span className="hidden sm:inline">
                {t("paymentModule.userPaymentHistory.refund")}
              </span>
            </span>
          }
          aria-label={
            t("paymentModule.userPaymentHistory.refundAria") as string
          }
        />
      )}
      {canCopyReceipt && canCreatePaymentLink && (
        <CopyToClipboardButton
          disabled={!canCreatePaymentLink}
          textToCopy={
            invoiceLinks[
              `${paymentData.invoiceIdentification}:${invoiceIntent}`
            ] ?? undefined
          }
          onBeforeCopy={() =>
            onFetchInvoiceLink(
              paymentData.invoiceIdentification!,
              invoiceIntent!
            )
          }
          label={
            <span className="hidden sm:inline">
              {
                t(
                  invoiceIntent === "payment"
                    ? "paymentHistory.invoice.copyPaymentLink"
                    : "paymentHistory.invoice.copyLink"
                ) as string
              }
            </span>
          }
          variant="none"
          size="sm"
          fullWidth={false}
          className={
            invoiceIntent === "payment"
              ? "text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg disabled:text-red-300 disabled:cursor-not-allowed"
              : "text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg disabled:text-blue-300 disabled:cursor-not-allowed"
          }
        />
      )}
    </div>
  );
};

export default PaymentStepActions;
